import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Save, Mail, Shield, Crown, Rocket, Send, RefreshCw, CheckCircle2, AlertTriangle, MessageSquare, Link2, Unlink, Activity, Trash2, Wrench, UploadCloud, LifeBuoy, ShieldCheck, Gift } from "lucide-react";
import BatchAckDiagnostic from "../components/BatchAckDiagnostic";
import { PrivacyDataCard } from "../components/PrivacyDataCard";
import { MediaRetentionClientCard } from "../components/MediaRetentionClientCard";

// Status → badge for the Magic Link upload diagnostics list.
const MAGIC_LINK_STATUS = {
  completed: { label: "Completato", cls: "bg-green-100 text-green-800" },
  recovered: { label: "Recuperato", cls: "bg-blue-100 text-blue-800" },
  pending: { label: "Bloccato a metà", cls: "bg-amber-100 text-amber-800" },
  abandoned: { label: "Mai arrivato", cls: "bg-red-100 text-red-800" },
  missing_on_dropbox: { label: "Non trovato", cls: "bg-red-100 text-red-800" },
  size_mismatch: { label: "File diverso", cls: "bg-red-100 text-red-800" },
  superseded: { label: "Duplicato", cls: "bg-neutral-200 text-neutral-700" },
  unknown: { label: "Sconosciuto", cls: "bg-neutral-200 text-neutral-700" },
};

export default function DashboardSettings() {
  const { user, fetchMe } = useAuth();
  const isClient = user?.role === "client";
  const isAdmin = user?.role === "admin";

  const [nameSalone, setNameSalone] = useState("");
  const [refNome, setRefNome] = useState("");
  const [refCognome, setRefCognome] = useState("");
  const [fullName, setFullName] = useState("");
  const [saving, setSaving] = useState(false);

  const [webhookInfo, setWebhookInfo] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookRecovering, setWebhookRecovering] = useState(false);
  const [webhookHealth, setWebhookHealth] = useState(null); // cached status from hourly cron
  const [healthChecking, setHealthChecking] = useState(false);
  const [systemHealth, setSystemHealth] = useState(null); // {webhook, dropbox, brevo, stories_delivery}
  const [systemHealthRunning, setSystemHealthRunning] = useState(false);
  const [brevoBackfill, setBrevoBackfill] = useState(null); // { synced, total_clients, errors_count } | null
  const [brevoBackfillRunning, setBrevoBackfillRunning] = useState(false);
  const [brevoTestEmail, setBrevoTestEmail] = useState("");
  const [brevoTestRunning, setBrevoTestRunning] = useState(false);
  // Offer tool admin config (v1.35)
  const [offerCfg, setOfferCfg] = useState(null); // {enabled, max_per_week, stats}
  const [offerCfgSaving, setOfferCfgSaving] = useState(false);
  // Permanent-link audit (v1.37)
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotNote, setSnapshotNote] = useState("");
  // Dropbox groups.json manifest (v1.41) — status of the downstream
  // sync worker. Only rendered when something is broken.
  const [manifestStatus, setManifestStatus] = useState(null);
  const [manifestRebuilding, setManifestRebuilding] = useState(false);
  const [brevoTestResult, setBrevoTestResult] = useState(null); // { ok, to, error? }

  const [healOrphansRunning, setHealOrphansRunning] = useState(false);
  const [healOrphansReport, setHealOrphansReport] = useState(null); // API response
  const [dedupeRunning, setDedupeRunning] = useState(false);
  const [dedupeReport, setDedupeReport] = useState(null);

  // Magic Link upload diagnostics (v1.22 — orphan rescue)
  const [mlTickets, setMlTickets] = useState(null); // { tickets, counts } | null
  const [mlLoading, setMlLoading] = useState(false);
  const [mlReconciling, setMlReconciling] = useState(false);
  const [mlHealth, setMlHealth] = useState(null);
  const [mlHealthLoading, setMlHealthLoading] = useState(false);

  // Staff internal chat removed 2026-05-09 — no related state needed.

  const loadWebhook = async () => {
    setWebhookLoading(true);
    try {
      const { data } = await api.get("/admin/telegram/webhook");
      setWebhookInfo(data?.info || null);
    } catch (e) {
      // silent - optional feature
    } finally {
      setWebhookLoading(false);
    }
  };

  const loadWebhookHealth = async () => {
    try {
      const { data } = await api.get("/admin/telegram/webhook/health");
      setWebhookHealth(data?.health || null);
    } catch {
      /* optional */
    }
  };

  const loadSystemHealth = async () => {
    try {
      const { data } = await api.get("/admin/system-health");
      setSystemHealth(data || null);
    } catch {
      /* optional */
    }
  };

  const runSystemHealthNow = async () => {
    setSystemHealthRunning(true);
    try {
      const { data } = await api.post("/admin/system-health/run");
      setSystemHealth(data || null);
      const broken = Object.entries(data || {})
        .filter(([, v]) => v && v.healthy === false)
        .map(([k]) => k);
      if (broken.length === 0) {
        toast.success("Tutti i sistemi OK ✓");
      } else {
        toast.warning(`Problemi rilevati: ${broken.join(", ")}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSystemHealthRunning(false);
    }
  };

  const runBrevoBackfill = async () => {
    if (
      !window.confirm(
        "Spingere TUTTI i clienti già registrati nelle liste Brevo (Free/Pro) " +
          "in base al loro piano attuale?\n\n" +
          "L'operazione è sicura e idempotente (puoi rilanciarla)."
      )
    ) {
      return;
    }
    setBrevoBackfillRunning(true);
    try {
      const { data } = await api.post("/admin/brevo/backfill-clients");
      setBrevoBackfill(data || null);
      if (data?.errors_count > 0) {
        toast.warning(
          `Sincronizzati ${data.synced}/${data.total_clients}. ${data.errors_count} errori.`
        );
      } else {
        toast.success(
          `Sincronizzati ${data?.synced || 0}/${data?.total_clients || 0} clienti su Brevo ✓`
        );
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBrevoBackfillRunning(false);
    }
  };

  const runBrevoTestEmail = async () => {
    const target = (brevoTestEmail || "").trim();
    if (!target) {
      toast.error("Inserisci un indirizzo email di destinazione");
      return;
    }
    setBrevoTestRunning(true);
    setBrevoTestResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { email: target });
      setBrevoTestResult({ ok: true, to: data?.to || target });
      toast.success(`Email di prova inviata a ${data?.to || target} ✓`);
    } catch (e) {
      const msg = apiError(e);
      setBrevoTestResult({ ok: false, to: target, error: msg });
      toast.error(msg);
    } finally {
      setBrevoTestRunning(false);
    }
  };

  const runHealOrphans = async (apply) => {
    if (apply) {
      const ok = window.confirm(
        "Confermi di voler APPLICARE la riparazione? " +
        "I messaggi orfani verranno riparentati sotto il gruppo del batch " +
        "originale. Operazione idempotente e reversibile."
      );
      if (!ok) return;
    }
    setHealOrphansRunning(true);
    if (!apply) setHealOrphansReport(null);
    try {
      const { data } = await api.post(
        `/admin/upload-link/heal-orphans?apply=${apply ? "true" : "false"}`
      );
      setHealOrphansReport(data);
      if (data.orphans_found === 0) {
        toast.success("Nessun video orfano trovato — tutto pulito ✓");
      } else if (apply) {
        toast.success(
          `Riparati ${data.healed} video (${data.unmatched} senza peer)`
        );
      } else {
        toast.message(
          `Dry-run: troverei ${data.healed} da riparare, ${data.unmatched} senza peer`
        );
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setHealOrphansRunning(false);
    }
  };

  const runDedupePlaceholders = async (apply) => {
    if (apply) {
      const ok = window.confirm(
        "Confermi la deduplica? Per ogni coppia placeholder + upload nello " +
        "stesso gruppo, il placeholder viene aggiornato col vero video e " +
        "l'upload duplicato cancellato. Operazione idempotente."
      );
      if (!ok) return;
    }
    setDedupeRunning(true);
    if (!apply) setDedupeReport(null);
    try {
      const { data } = await api.post(
        `/admin/upload-link/dedupe-placeholders?apply=${apply ? "true" : "false"}`
      );
      setDedupeReport(data);
      if (data.pairs_matched === 0) {
        toast.success("Nessuna coppia da deduplicare — tutto pulito ✓");
      } else if (apply) {
        toast.success(`Fuse ${data.pairs_matched} coppie duplicate`);
      } else {
        toast.message(
          `Dry-run: troverei ${data.pairs_matched} coppie da fondere in ${data.groups_scanned} gruppi`
        );
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDedupeRunning(false);
    }
  };

  const loadMlTickets = async () => {
    setMlLoading(true);
    try {
      const { data } = await api.get("/admin/upload-link/tickets?limit=60");
      setMlTickets(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMlLoading(false);
    }
  };

  const runMlReconcile = async () => {
    setMlReconciling(true);
    try {
      const { data } = await api.post("/admin/upload-link/reconcile");
      if ((data.recovered_count || 0) > 0) {
        toast.success(
          `Recuperati ${data.recovered_count} video orfani — ora visibili in Produzione ✓`
        );
      } else {
        toast.message(
          `Nessun video orfano da recuperare (analizzati ${data.scanned} upload in sospeso)`
        );
      }
      await loadMlTickets();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMlReconciling(false);
    }
  };

  const runMlHealth = async () => {
    setMlHealthLoading(true);
    try {
      const { data } = await api.get("/admin/upload-link/health");
      setMlHealth(data);
      if (data.ok) {
        toast.success(
          `Tutto in ordine: ${data.checked_tokens} link permanenti verificati ✓`
        );
      } else {
        toast.warning("Trovati problemi sui link — vedi il dettaglio qui sotto");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMlHealthLoading(false);
    }
  };

  const runHealthcheckNow = async () => {
    setHealthChecking(true);
    try {
      const { data } = await api.post("/admin/telegram/webhook/healthcheck");
      setWebhookHealth(data || null);
      if (data?.skipped) {
        toast.message("Controllo saltato: questo non è il pod di produzione");
      } else if (data?.healed) {
        toast.success("Webhook deviato → ho corretto automaticamente");
        await loadWebhook();
      } else if (data?.healthy) {
        toast.success("Webhook OK — nessun problema rilevato");
      } else {
        toast.warning("Webhook con anomalie — controlla i dettagli");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setHealthChecking(false);
    }
  };

  const registerWebhook = async () => {
    setWebhookSaving(true);
    try {
      // Pass current window origin explicitly so the backend registers the
      // exact domain the admin is using (works on preview, prod, or custom domain).
      const { data } = await api.post("/admin/telegram/webhook", {
        base_url: window.location.origin,
      });
      toast.success(`Webhook aggiornato: ${data.webhook_url}`);
      await loadWebhook();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWebhookSaving(false);
    }
  };

  const recoverWebhook = async () => {
    // Forced re-register with `drop_pending_updates=true`. Used after a
    // backend outage that left Telegram in suspended-delivery mode.
    if (!window.confirm(
      "Sto per forzare il riavvio del webhook Telegram con DROP dei pending updates. "
      + "I messaggi accumulati durante l'outage andranno persi (probabilmente scaduti comunque). "
      + "Procedo?"
    )) return;
    setWebhookRecovering(true);
    try {
      const { data } = await api.post("/admin/telegram/webhook/recover");
      const before = data?.before?.pending_update_count ?? "?";
      const after = data?.after?.pending_update_count ?? "?";
      toast.success(
        `Webhook recuperato. Pending: ${before} → ${after}. I nuovi video dovrebbero ora arrivare normalmente.`,
        { duration: 8000 },
      );
      await loadWebhook();
      await runHealthcheckNow();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWebhookRecovering(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    setNameSalone(user.name_salone || "");
    setRefNome(user.referente_nome || "");
    setRefCognome(user.referente_cognome || "");
    setFullName(user.full_name || "");
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    loadWebhook();
    loadWebhookHealth();
    loadSystemHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const dirty = isClient
    ? nameSalone !== (user?.name_salone || "") ||
      refNome !== (user?.referente_nome || "") ||
      refCognome !== (user?.referente_cognome || "")
    : fullName !== (user?.full_name || "");

  const save = async () => {
    setSaving(true);
    try {
      const payload = isClient
        ? {
            name_salone: nameSalone,
            referente_nome: refNome,
            referente_cognome: refCognome,
          }
        : { full_name: fullName };
      await api.patch("/auth/me", payload);
      await fetchMe();
      toast.success("Dati aggiornati");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const roleLabel =
    user?.role === "admin"
      ? "Amministratore"
      : user?.role === "agency"
        ? "Agenzia"
        : "Cliente";

  // Load offer tool config on mount (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/offer/config");
        setOfferCfg(data);
      } catch (e) {
        // Non-blocking
      }
    })();
  }, [isAdmin]);

  const saveOfferConfig = async (patch) => {
    setOfferCfgSaving(true);
    try {
      const { data } = await api.patch("/admin/offer/config", patch);
      setOfferCfg(data);
      toast.success("Impostazioni offerta aggiornate");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setOfferCfgSaving(false);
    }
  };

  // Permanent-link audit (v1.37) — verifies that no client's upload
  // link has changed since the admin last froze a baseline snapshot.
  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get("/admin/upload-link/audit");
      setAuditData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAuditLoading(false);
    }
  };

  const freezeSnapshot = async () => {
    if (!window.confirm(
      "Sicuro di voler fissare l'attuale situazione come baseline? " +
      "Ogni futura variazione di link sarà segnalata come 'changed'."
    )) return;
    setSnapshotSaving(true);
    try {
      const { data } = await api.post("/admin/upload-link/audit/snapshot", {
        note: snapshotNote.trim() || "baseline",
      });
      toast.success(`Baseline salvato · ${data.count} link congelati`);
      setSnapshotNote("");
      await runAudit();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSnapshotSaving(false);
    }
  };

  useEffect(() => {
    if (isAdmin) runAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // v1.41 — poll manifest health every 60s (only when admin is viewing
  // this page). Cheap: one Mongo count + one error read.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/manifest/status");
        if (!cancelled) setManifestStatus(data);
      } catch (_) {
        /* silent — not worth a toast every minute */
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [isAdmin]);

  // v1.41.1 — one-shot bootstrap of every client/day folder's manifest.
  // Fire-and-forget: the endpoint returns immediately after queueing,
  // the actual Dropbox writes stream through the 2s-tick worker.
  const rebuildAllManifests = async () => {
    if (manifestRebuilding) return;
    setManifestRebuilding(true);
    try {
      const { data } = await api.post("/admin/manifest/rebuild-all");
      toast.success(
        `Manifest in coda: ${data.folders_queued} cartell${data.folders_queued === 1 ? "a" : "e"} · ${data.messages_considered} file`,
        {
          description:
            "Le scritture su Dropbox avvengono in background (~2s per batch). Controlla i log qui sotto tra qualche secondo.",
        },
      );
      // Refresh status after a beat so the pending count reflects the new work.
      setTimeout(async () => {
        try {
          const { data: s } = await api.get("/admin/manifest/status");
          setManifestStatus(s);
        } catch (_) {
          /* ignore */
        }
      }, 800);
    } catch (e) {
      toast.error(apiError(e) || "Rigenerazione manifest fallita");
    } finally {
      setManifestRebuilding(false);
    }
  };

  return (
    <div data-testid="dashboard-settings" className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Impostazioni
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Il tuo account
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-xl">
          Modifica i tuoi dati. Email, password e ruolo non sono modificabili da
          qui: per cambiarli contatta l'agenzia.
        </p>
      </div>

      {/* Editable fields */}
      <div
        data-testid="settings-editable-card"
        className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-5"
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
          Dati modificabili
        </div>

        {isClient ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldBlock
              label="Nome del salone"
              testId="settings-salone"
              value={nameSalone}
              onChange={setNameSalone}
              placeholder="Es: Salone Maria"
            />
            <FieldBlock
              label="Nome referente"
              testId="settings-ref-nome"
              value={refNome}
              onChange={setRefNome}
              placeholder="Es: Mario"
            />
            <FieldBlock
              label="Cognome referente"
              testId="settings-ref-cognome"
              value={refCognome}
              onChange={setRefCognome}
              placeholder="Es: Rossi"
            />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldBlock
              label="Nome e cognome"
              testId="settings-full-name"
              value={fullName}
              onChange={setFullName}
              placeholder="Es: Giulia Bianchi"
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            data-testid="settings-save-btn"
            onClick={save}
            disabled={!dirty || saving}
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Salvataggio…" : "Salva modifiche"}
          </Button>
        </div>
      </div>

      {/* Admin-only: System health dashboard (webhook + dropbox + brevo + stories) */}
      {isAdmin && (
        <div
          data-testid="settings-system-health-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-5"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
              <Activity className="h-3.5 w-3.5" /> Stato sistema
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={runSystemHealthNow}
              disabled={systemHealthRunning}
              data-testid="settings-system-health-run"
              className="rounded-full border-[var(--mp-ink)] h-8 text-[11px]"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${systemHealthRunning ? "animate-spin" : ""}`} />
              {systemHealthRunning ? "Controllo…" : "Controlla ora"}
            </Button>
          </div>
          <div className="text-sm text-[var(--mp-muted)] leading-relaxed">
            Monitoraggio automatico delle integrazioni critiche. Se qualcosa
            smette di funzionare, ricevi una notifica sulla <strong>chat
            Telegram staff</strong> collegata qui sotto.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: "webhook", label: "Webhook Telegram", desc: "Consegna messaggi clienti" },
              { key: "dropbox", label: "Dropbox", desc: "Archivio file e loghi" },
              { key: "brevo", label: "Brevo", desc: "Email transazionali" },
              { key: "stories_delivery", label: "Idee storie AI", desc: "Consegna giornaliera 09:00 (Pro)" },
            ].map(({ key, label, desc }) => {
              const s = systemHealth?.[key] || {};
              const hasData = !!s.checked_at;
              const healthy = s.healthy === true;
              const unhealthy = s.healthy === false;
              return (
                <div
                  key={key}
                  data-testid={`system-health-${key}`}
                  className={`rounded-2xl border p-3.5 space-y-1.5 ${
                    unhealthy
                      ? "border-red-300 bg-red-50"
                      : healthy
                      ? "border-green-200 bg-green-50/40"
                      : "border-[var(--mp-border)] bg-[var(--mp-dash-bg)]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {unhealthy ? (
                      <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                    ) : healthy ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    ) : (
                      <Activity className="h-4 w-4 text-[var(--mp-muted)] shrink-0" />
                    )}
                    <div className="font-bold text-[13px]">{label}</div>
                  </div>
                  <div className="text-[11px] text-[var(--mp-muted)]">{desc}</div>
                  {hasData && (
                    <div
                      className={`text-[11px] leading-snug ${
                        unhealthy ? "text-red-700" : "text-[var(--mp-muted)]"
                      }`}
                    >
                      {s.message || (healthy ? "OK" : "Anomalia rilevata")}
                    </div>
                  )}
                  {hasData && (
                    <div className="text-[10px] text-[var(--mp-muted)] italic">
                      Controllato:{" "}
                      {new Date(s.checked_at).toLocaleString("it-IT", {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-[11px] text-[var(--mp-muted)] italic border-t border-[var(--mp-border)] pt-3">
            Webhook + Dropbox + Brevo: controllo ogni ora. Idee storie: controllo giornaliero alle 11:00.
            Nessun costo aggiuntivo.
          </div>
        </div>
      )}

      {/* Admin-only: Brevo backfill (push all existing clients to Brevo lists) */}
      {isAdmin && (
        <div
          data-testid="brevo-backfill-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                Brevo · Sincronizzazione liste
              </div>
              <div className="font-display text-xl tracking-tight">
                Spingi tutti i clienti già registrati su Brevo
              </div>
              <div className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
                Aggiunge ogni cliente alla lista <strong>Portale Clienti Free</strong> o{" "}
                <strong>Portale Clienti Pro</strong> in base al piano attuale,
                con attributi <code>NOME</code>, <code>COGNOME</code>,{" "}
                <code>SALONE</code>, <code>STATUS</code>, <code>PLAN</code>.
                Operazione sicura e ripetibile: aggiorna i contatti esistenti
                senza duplicarli.
              </div>
            </div>
            <Button
              data-testid="brevo-backfill-btn"
              onClick={runBrevoBackfill}
              disabled={brevoBackfillRunning}
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1.5 ${
                  brevoBackfillRunning ? "animate-spin" : ""
                }`}
              />
              {brevoBackfillRunning ? "Sincronizzo…" : "Sincronizza tutti ora"}
            </Button>
          </div>
          {brevoBackfill && (
            <div
              data-testid="brevo-backfill-result"
              className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4 text-sm"
            >
              <div className="font-bold mb-1">Risultato ultima sincronizzazione:</div>
              <div>
                Clienti totali: <strong>{brevoBackfill.total_clients}</strong> ·
                Sincronizzati con successo:{" "}
                <strong className="text-green-700">{brevoBackfill.synced}</strong> ·
                Errori:{" "}
                <strong
                  className={
                    brevoBackfill.errors_count > 0 ? "text-red-700" : ""
                  }
                >
                  {brevoBackfill.errors_count}
                </strong>
              </div>
              {brevoBackfill.errors_count > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[12px] text-[var(--mp-muted)]">
                    Mostra primi {brevoBackfill.errors?.length || 0} errori
                  </summary>
                  <ul className="mt-2 list-disc pl-5 text-[12px] text-red-700 space-y-0.5">
                    {(brevoBackfill.errors || []).map((err, i) => (
                      <li key={`${i}-${err}`}>{err}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          <div className="text-[11px] text-[var(--mp-muted)] italic">
            Da usare in produzione una volta sola dopo aver attivato il sync
            Brevo. I nuovi clienti che si registrano vengono già aggiunti
            automaticamente.
          </div>
        </div>
      )}

      {/* Admin-only: Brevo test email (verify end-to-end delivery) */}
      {isAdmin && (
        <div
          data-testid="brevo-test-email-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
        >
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
              Brevo · Test invio reale
            </div>
            <div className="font-display text-xl tracking-tight">
              Verifica che le email transazionali partano davvero
            </div>
            <div className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
              Invia una <strong>welcome email di prova</strong> all&apos;indirizzo
              indicato. Utile per confermare che Brevo funzioni end-to-end anche
              quando il monitor <code>/v3/account</code> mostra warning.
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder={user?.email || "email@dominio.it"}
              value={brevoTestEmail}
              onChange={(e) => setBrevoTestEmail(e.target.value)}
              data-testid="brevo-test-email-input"
              className="flex-1"
            />
            <Button
              data-testid="brevo-test-email-btn"
              onClick={runBrevoTestEmail}
              disabled={brevoTestRunning}
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <Send
                className={`h-3.5 w-3.5 mr-1.5 ${brevoTestRunning ? "animate-pulse" : ""}`}
              />
              {brevoTestRunning ? "Invio in corso…" : "Invia email di prova"}
            </Button>
          </div>
          {brevoTestResult && (
            <div
              data-testid="brevo-test-email-result"
              className={`rounded-2xl border p-3 text-sm ${
                brevoTestResult.ok
                  ? "border-green-300 bg-green-50 text-green-900"
                  : "border-red-300 bg-red-50 text-red-900"
              }`}
            >
              {brevoTestResult.ok ? (
                <>
                  <CheckCircle2 className="inline h-4 w-4 mr-1" />
                  Email inviata a <strong>{brevoTestResult.to}</strong>. Controlla
                  la casella (anche spam) — se arriva, il flusso transazionale
                  Brevo è OK.
                </>
              ) : (
                <>
                  <AlertTriangle className="inline h-4 w-4 mr-1" />
                  Invio fallito: {brevoTestResult.error}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Admin-only: heal orphan Magic Link uploads (v1.21.5 remediation) */}
      {isAdmin && (
        <div
          data-testid="heal-orphans-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
        >
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
              <Wrench className="h-3.5 w-3.5" /> Ripara Magic Link · gruppi orfani
            </div>
            <div className="font-display text-xl tracking-tight">
              Ricuci i video caricati via Magic Link non ancora raggruppati
            </div>
            <div className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
              Fino alla v1.21.5 c&apos;era un bug per cui i video caricati dai
              clienti tramite il portale (dopo il magic link) restavano
              scollegati dal batch Telegram originale. Questo pulsante li
              re-parenta sotto il gruppo del batch corretto (cercando un peer{" "}
              <code>too_large</code> entro ±30 min). Idempotente — puoi
              lanciarlo più volte in sicurezza.
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              data-testid="heal-orphans-dryrun-btn"
              onClick={() => runHealOrphans(false)}
              disabled={healOrphansRunning}
              variant="outline"
              className="rounded-full border-[var(--mp-ink)]"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1.5 ${healOrphansRunning ? "animate-spin" : ""}`}
              />
              {healOrphansRunning ? "Analisi in corso…" : "1. Dry-run (analizza)"}
            </Button>
            <Button
              data-testid="heal-orphans-apply-btn"
              onClick={() => runHealOrphans(true)}
              disabled={
                healOrphansRunning ||
                !healOrphansReport ||
                healOrphansReport?.applied ||
                (healOrphansReport?.healed || 0) === 0
              }
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              2. Applica riparazione
            </Button>
          </div>
          {healOrphansReport && (
            <div
              data-testid="heal-orphans-report"
              className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4 text-sm space-y-3"
            >
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="bg-[var(--mp-ink)] text-white">
                  {healOrphansReport.applied ? "APPLICATO" : "DRY-RUN"}
                </Badge>
                <span>
                  Orfani trovati: <strong>{healOrphansReport.orphans_found}</strong>
                </span>
                <span className="text-green-700">
                  {healOrphansReport.applied ? "Riparati" : "Da riparare"}:{" "}
                  <strong>{healOrphansReport.healed}</strong>
                </span>
                <span className="text-amber-700">
                  Senza peer: <strong>{healOrphansReport.unmatched}</strong>
                </span>
              </div>
              {Object.keys(healOrphansReport.per_client || {}).length > 0 && (
                <div className="max-h-72 overflow-y-auto space-y-3">
                  {Object.entries(healOrphansReport.per_client).map(
                    ([cid, info]) => (
                      <div
                        key={cid}
                        className="rounded-xl border border-[var(--mp-border)] bg-white p-3"
                      >
                        <div className="font-semibold text-[13px] mb-1">
                          {info.client_name}
                        </div>
                        {info.healed_items?.length > 0 && (
                          <div className="text-[12px] text-green-800 space-y-0.5">
                            {info.healed_items.map((it, i) => (
                              <div key={i}>
                                ✓ {it.file_name}
                                {it.group_name && (
                                  <span className="text-[var(--mp-muted)]">
                                    {" "}
                                    → {it.group_name}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {info.unmatched_items?.length > 0 && (
                          <div className="text-[12px] text-amber-800 space-y-0.5 mt-1">
                            {info.unmatched_items.map((it, i) => (
                              <div key={i}>
                                ⚠ {it.file_name}{" "}
                                <span className="text-[var(--mp-muted)]">
                                  ({it.reason})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
              {healOrphansReport.orphans_found === 0 && (
                <div className="text-[13px] text-green-700">
                  <CheckCircle2 className="inline h-4 w-4 mr-1" />
                  Tutti i Magic Link uploads sono correttamente raggruppati.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Admin-only: dedupe placeholder + upload_link pairs (v1.21.6) */}
      {isAdmin && (
        <div
          data-testid="dedupe-placeholders-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
        >
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
              <Wrench className="h-3.5 w-3.5" /> Deduplica placeholder Telegram + Magic Link
            </div>
            <div className="font-display text-xl tracking-tight">
              Fondi le coppie duplicate lasciate dai vecchi upload
            </div>
            <div className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
              Nei gruppi &quot;7 clip&quot; con 5 rettangoli vuoti + 2 miniature vere:
              per ogni coppia (placeholder Telegram + upload dal magic link)
              il placeholder eredita il vero video e l&apos;upload duplicato
              viene cancellato. Match per nome file esatto prima, FIFO dopo.
              Idempotente. Da v1.21.6 in avanti il consumo avviene
              automaticamente al momento dell&apos;upload.
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              data-testid="dedupe-placeholders-dryrun-btn"
              onClick={() => runDedupePlaceholders(false)}
              disabled={dedupeRunning}
              variant="outline"
              className="rounded-full border-[var(--mp-ink)]"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1.5 ${dedupeRunning ? "animate-spin" : ""}`}
              />
              {dedupeRunning ? "Analisi in corso…" : "1. Dry-run (analizza)"}
            </Button>
            <Button
              data-testid="dedupe-placeholders-apply-btn"
              onClick={() => runDedupePlaceholders(true)}
              disabled={
                dedupeRunning ||
                !dedupeReport ||
                dedupeReport?.applied ||
                (dedupeReport?.pairs_matched || 0) === 0
              }
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              2. Applica deduplica
            </Button>
          </div>
          {dedupeReport && (
            <div
              data-testid="dedupe-placeholders-report"
              className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4 text-sm space-y-3"
            >
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="bg-[var(--mp-ink)] text-white">
                  {dedupeReport.applied ? "APPLICATO" : "DRY-RUN"}
                </Badge>
                <span>
                  Gruppi analizzati: <strong>{dedupeReport.groups_scanned}</strong>
                </span>
                <span className="text-green-700">
                  Coppie {dedupeReport.applied ? "fuse" : "da fondere"}:{" "}
                  <strong>{dedupeReport.pairs_matched}</strong>
                </span>
                <span className="text-amber-700">
                  Placeholder rimanenti:{" "}
                  <strong>{dedupeReport.orphan_placeholders}</strong>
                </span>
              </div>
              {Object.keys(dedupeReport.per_group || {}).length > 0 && (
                <div className="max-h-72 overflow-y-auto space-y-3">
                  {Object.entries(dedupeReport.per_group).map(([gid, info]) => (
                    <div
                      key={gid}
                      className="rounded-xl border border-[var(--mp-border)] bg-white p-3"
                    >
                      <div className="font-semibold text-[13px] mb-1">
                        {info.client_name}
                        <span className="text-[var(--mp-muted)] font-normal">
                          {" "}
                          · {info.group_name}
                        </span>
                      </div>
                      <div className="text-[12px] space-y-0.5">
                        {info.pairs?.map((p, i) => (
                          <div key={i} className="text-green-800">
                            ✓ {p.placeholder_file}
                            {p.matched_by === "filename" ? (
                              <span className="text-[var(--mp-muted)]"> (match esatto)</span>
                            ) : (
                              <span className="text-[var(--mp-muted)]">
                                {" "}
                                → {p.upload_file} (FIFO)
                              </span>
                            )}
                          </div>
                        ))}
                        {(info.orphan_placeholders > 0 ||
                          info.orphan_uploads > 0) && (
                          <div className="text-amber-700 mt-1">
                            {info.orphan_placeholders > 0 && (
                              <span>
                                ⚠ {info.orphan_placeholders} placeholder senza
                                upload (cliente non li ha ricaricati).
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Admin-only: Creatore Offerte Irresistibili config (v1.35) */}
      {isAdmin && (
        <div
          data-testid="offer-tool-admin-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
        >
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
              <Gift className="h-3.5 w-3.5" /> Creatore Offerte Irresistibili
            </div>
            <div className="font-display text-xl tracking-tight">
              Strumento AI per i clienti con posizionamento completo
            </div>
            <div className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
              Genera un'offerta marketing coerente col posizionamento del
              salone (Claude Haiku 4.5). Da qui puoi attivarlo/disattivarlo
              per tutti i clienti e regolare il numero massimo di
              generazioni settimanali per utente.
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="offer-tool-enabled-toggle"
                checked={Boolean(offerCfg?.enabled)}
                disabled={!offerCfg || offerCfgSaving}
                onChange={(e) => saveOfferConfig({ enabled: e.target.checked })}
                className="h-4 w-4 accent-[var(--mp-ink)]"
              />
              <span className="text-sm font-semibold">
                {offerCfg?.enabled ? "Attivo per i clienti" : "Disattivato"}
              </span>
            </label>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--mp-muted)] mb-1">
                Max settimana · Free
              </div>
              <div className="inline-flex items-center gap-2">
                <Input
                  data-testid="offer-tool-max-per-week-free"
                  type="number"
                  min="1"
                  max="20"
                  value={offerCfg?.max_per_week_free ?? 1}
                  onChange={(e) =>
                    setOfferCfg((s) => ({
                      ...(s || {}),
                      max_per_week_free: Number(e.target.value),
                    }))
                  }
                  className="w-20 rounded-full border-[var(--mp-ink)] h-9"
                />
                <Button
                  data-testid="offer-tool-save-max-free"
                  onClick={() =>
                    saveOfferConfig({
                      max_per_week_free: offerCfg?.max_per_week_free ?? 1,
                    })
                  }
                  disabled={offerCfgSaving}
                  size="sm"
                  className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                >
                  <Save className="h-3.5 w-3.5 mr-1" /> Salva
                </Button>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--mp-muted)] mb-1">
                Max settimana · Pro
              </div>
              <div className="inline-flex items-center gap-2">
                <Input
                  data-testid="offer-tool-max-per-week-pro"
                  type="number"
                  min="1"
                  max="20"
                  value={offerCfg?.max_per_week_pro ?? 3}
                  onChange={(e) =>
                    setOfferCfg((s) => ({
                      ...(s || {}),
                      max_per_week_pro: Number(e.target.value),
                    }))
                  }
                  className="w-20 rounded-full border-[var(--mp-ink)] h-9"
                />
                <Button
                  data-testid="offer-tool-save-max-pro"
                  onClick={() =>
                    saveOfferConfig({
                      max_per_week_pro: offerCfg?.max_per_week_pro ?? 3,
                    })
                  }
                  disabled={offerCfgSaving}
                  size="sm"
                  className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                >
                  <Save className="h-3.5 w-3.5 mr-1" /> Salva
                </Button>
              </div>
            </div>
          </div>
          {offerCfg?.stats && (
            <div className="rounded-2xl bg-[var(--mp-dash-bg)] p-3 text-xs text-[var(--mp-muted)] inline-flex gap-4">
              <span>Offerte totali: <strong className="text-[var(--mp-ink)]">{offerCfg.stats.total_generations}</strong></span>
              <span>Ultimi 7 giorni: <strong className="text-[var(--mp-ink)]">{offerCfg.stats.generations_last_7d}</strong></span>
            </div>
          )}
        </div>
      )}

      {/* Admin-only: Dropbox manifest health (v1.41+v1.41.1). Card with
          the "Rebuild all" action always visible; RED banner overlay
          only when errors surface. Bundle-together for cognitive
          proximity — everything about groups.json in one place. */}
      {isAdmin && (
        <div
          data-testid="manifest-admin-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
        >
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
              <Activity className="h-3.5 w-3.5" /> Manifest Dropbox
            </div>
            <div className="font-display text-xl tracking-tight">
              Sincronizzazione groups.json col sistema di montaggio
            </div>
            <div className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
              Ogni cartella cliente/giorno su Dropbox contiene un file
              <code className="mx-1 px-1 rounded bg-[var(--mp-dash-bg)] font-mono text-xs">groups.json</code>
              che il sistema di montaggio a valle legge per capire come
              raggruppare le clip. Si aggiorna in automatico ad ogni
              cambiamento; usa il bottone qui sotto se il montatore
              segnala manifest disallineati o dopo un downtime prolungato
              di Dropbox.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              data-testid="manifest-rebuild-all-btn"
              onClick={rebuildAllManifests}
              disabled={manifestRebuilding}
              title="Da usare dopo un downtime prolungato di Dropbox o se il montatore a valle segnala manifest disallineati"
              className="bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              {manifestRebuilding ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  In coda…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Rigenera tutti i manifest
                </>
              )}
            </Button>
            {manifestStatus && (
              <div
                data-testid="manifest-live-status"
                className="text-xs text-[var(--mp-muted)] flex items-center gap-3"
              >
                <span>
                  Dropbox:{" "}
                  <strong className={manifestStatus.configured ? "text-green-700" : "text-red-700"}>
                    {manifestStatus.configured ? "configurato" : "non configurato"}
                  </strong>
                </span>
                <span>·</span>
                <span>
                  In coda:{" "}
                  <strong className={manifestStatus.pending_count > 0 ? "text-amber-700" : "text-[var(--mp-ink)]"}>
                    {manifestStatus.pending_count}
                  </strong>
                </span>
                <span>·</span>
                <span>
                  Errori:{" "}
                  <strong className={(manifestStatus.errors?.length || 0) > 0 ? "text-red-700" : "text-green-700"}>
                    {manifestStatus.errors?.length || 0}
                  </strong>
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin-only: Dropbox manifest health (v1.41) — visible only
          when the downstream groups.json sync is unhealthy so the
          admin sees an alarm instead of just silence. */}
      {isAdmin && manifestStatus && (manifestStatus.errors?.length > 0 || manifestStatus.pending_count > 5) && (
        <div
          data-testid="manifest-health-banner"
          className={`rounded-3xl border-2 shadow-brutal-sm p-5 space-y-3 ${
            manifestStatus.errors?.length > 0
              ? "border-red-500 bg-red-50"
              : "border-amber-400 bg-amber-50"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 h-9 w-9 rounded-full grid place-items-center ${
              manifestStatus.errors?.length > 0
                ? "bg-red-600 text-white"
                : "bg-amber-500 text-white"
            }`}>
              <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg leading-tight">
                {manifestStatus.errors?.length > 0
                  ? "Manifest Dropbox: scritture fallite"
                  : "Manifest Dropbox: coda in ritardo"}
              </div>
              <p className="text-sm mt-1 leading-relaxed">
                {manifestStatus.errors?.length > 0
                  ? `Il sistema di montaggio a valle potrebbe non vedere i gruppi aggiornati. ${manifestStatus.errors.length} cartell${manifestStatus.errors.length === 1 ? "a" : "e"} in errore.`
                  : `${manifestStatus.pending_count} cartelle in coda per la sincronizzazione — se non scende entro qualche minuto, Dropbox potrebbe essere lento o non configurato.`}
              </p>
              {manifestStatus.errors?.length > 0 && (
                <ul
                  data-testid="manifest-errors-list"
                  className="mt-2 space-y-1 text-xs text-red-900"
                >
                  {manifestStatus.errors.slice(0, 5).map((e) => (
                    <li key={e.folder} className="font-mono truncate">
                      <span className="opacity-70">×{e.count}</span> {e.folder}
                      <span className="opacity-70"> — {e.error?.slice(0, 80)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin-only: Permanent-link fleet audit (v1.37) — proves that
          every magic link is still the same one the client received. */}
      {isAdmin && (
        <div
          data-testid="upload-link-audit-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
        >
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Integrità Magic Link
            </div>
            <div className="font-display text-xl tracking-tight">
              I link personali dei clienti non sono mai cambiati?
            </div>
            <div className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
              Ogni link ha un&apos;impronta digitale (fingerprint). Fissa
              l&apos;impronta di oggi come baseline: se domani un link viene
              rigenerato — anche per sbaglio — te ne accorgi subito.
            </div>
          </div>

          {auditLoading && !auditData ? (
            <div className="rounded-2xl bg-[var(--mp-dash-bg)] p-4 text-sm text-[var(--mp-muted)]">
              Caricamento audit…
            </div>
          ) : auditData ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Link permanenti" value={auditData.total_permanent} tone="ok" testId="audit-stat-permanent" />
                <Stat label="Link temporanei" value={auditData.total_temporary} tone={auditData.total_temporary === 0 ? "ok" : "warn"} testId="audit-stat-temporary" />
                <Stat label="Scaduti/Revocati" value={auditData.total_expired_or_revoked} tone="muted" testId="audit-stat-expired" />
                <Stat label="Duplicati" value={auditData.duplicate_owners.length} tone={auditData.duplicate_owners.length === 0 ? "ok" : "warn"} testId="audit-stat-duplicates" />
              </div>

              <div
                data-testid="audit-fingerprint-box"
                className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-3"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1">
                  Fingerprint attuale
                </div>
                <div className="font-mono text-xs break-all text-[var(--mp-ink)]">
                  {auditData.fingerprint}
                </div>
              </div>

              {auditData.baseline_available && auditData.diff ? (
                <div
                  data-testid="audit-diff-box"
                  className={`rounded-2xl border-2 p-4 text-sm ${
                    auditData.diff.status === "unchanged"
                      ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                      : "border-red-500 bg-red-50 text-red-900"
                  }`}
                >
                  <div className="font-bold flex items-center gap-2">
                    {auditData.diff.status === "unchanged" ? (
                      <><CheckCircle2 className="h-4 w-4" /> Tutti i link sono identici alla baseline</>
                    ) : (
                      <><AlertTriangle className="h-4 w-4" /> Attenzione: qualcosa è cambiato</>
                    )}
                  </div>
                  <div className="text-xs mt-1 opacity-80">
                    Baseline del {new Date(auditData.diff.baseline_saved_at).toLocaleString("it-IT")} —
                    &quot;{auditData.diff.baseline_note || "senza note"}&quot;
                  </div>
                  {auditData.diff.status !== "unchanged" && (
                    <div className="mt-3 space-y-1 text-xs" data-testid="audit-diff-details">
                      {auditData.diff.changed.length > 0 && (
                        <div><strong>{auditData.diff.changed.length} rigenerati</strong>:
                          {auditData.diff.changed.slice(0, 5).map((c, i) => (
                            <div key={i} className="ml-2 font-mono">
                              client {c.client_id.slice(-6)}: {c.was} → {c.now}
                            </div>
                          ))}
                        </div>
                      )}
                      {auditData.diff.added.length > 0 && (
                        <div><strong>{auditData.diff.added.length} nuovi</strong> (link creati dopo la baseline)</div>
                      )}
                      {auditData.diff.removed.length > 0 && (
                        <div><strong>{auditData.diff.removed.length} spariti</strong> (revocati o cancellati)</div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  data-testid="audit-no-baseline-notice"
                  className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                >
                  <div className="font-bold">Nessuna baseline salvata</div>
                  <div className="text-xs mt-1">
                    Fissa lo stato attuale come baseline per iniziare a
                    monitorare eventuali cambiamenti.
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--mp-muted)] mb-1">
                    Nota per la baseline (facoltativa)
                  </div>
                  <Input
                    data-testid="audit-snapshot-note"
                    value={snapshotNote}
                    onChange={(e) => setSnapshotNote(e.target.value)}
                    placeholder={`Es. "produzione ${new Date().toLocaleDateString("it-IT")}"`}
                    maxLength={200}
                    className="rounded-full border-[var(--mp-ink)] h-10 text-sm"
                  />
                </div>
                <Button
                  data-testid="audit-freeze-snapshot"
                  onClick={freezeSnapshot}
                  disabled={snapshotSaving}
                  className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white h-10"
                >
                  <ShieldCheck className="h-4 w-4 mr-1" />
                  {snapshotSaving ? "Salvataggio…" : "Fissa baseline"}
                </Button>
                <Button
                  data-testid="audit-refresh"
                  onClick={runAudit}
                  variant="outline"
                  disabled={auditLoading}
                  className="rounded-full border-[var(--mp-ink)] h-10"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${auditLoading ? "animate-spin" : ""}`} />
                  Ricontrolla
                </Button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Admin-only: Magic Link upload diagnostics + orphan rescue (v1.22) */}
      {isAdmin && (
        <div
          data-testid="magic-link-diagnostics-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
        >
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
              <UploadCloud className="h-3.5 w-3.5" /> Diagnostica Magic Link
            </div>
            <div className="font-display text-xl tracking-tight">
              Cosa è successo agli upload dei clienti
            </div>
            <div className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
              Ogni tentativo di caricamento via Magic Link lascia una traccia
              (ultimi 7 giorni). Se un cliente dice &quot;te l&apos;ho
              mandato&quot; ma il video non si vede, qui scopri se l&apos;upload
              è morto a metà, non è mai partito, o è arrivato su Dropbox senza
              registrarsi. Il pulsante <em>Recupera orfani</em> aggancia subito
              i file arrivati su Dropbox ma senza card in Produzione (lo stesso
              controllo gira comunque in automatico ogni ora).
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              data-testid="ml-tickets-refresh-btn"
              onClick={loadMlTickets}
              disabled={mlLoading}
              variant="outline"
              className="rounded-full border-[var(--mp-ink)]"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1.5 ${mlLoading ? "animate-spin" : ""}`}
              />
              {mlLoading ? "Caricamento…" : mlTickets ? "Aggiorna elenco" : "Mostra upload recenti"}
            </Button>
            <Button
              data-testid="ml-reconcile-btn"
              onClick={runMlReconcile}
              disabled={mlReconciling}
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <LifeBuoy
                className={`h-3.5 w-3.5 mr-1.5 ${mlReconciling ? "animate-spin" : ""}`}
              />
              {mlReconciling ? "Recupero in corso…" : "Recupera orfani ora"}
            </Button>
            <Button
              data-testid="ml-health-btn"
              onClick={runMlHealth}
              disabled={mlHealthLoading}
              variant="outline"
              className="rounded-full border-[var(--mp-ink)]"
            >
              <ShieldCheck
                className={`h-3.5 w-3.5 mr-1.5 ${mlHealthLoading ? "animate-pulse" : ""}`}
              />
              {mlHealthLoading ? "Verifica…" : "Verifica link permanenti"}
            </Button>
          </div>
          {mlHealth && !mlHealth.ok && (
            <div
              data-testid="ml-health-issues"
              className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm space-y-2"
            >
              {mlHealth.revoked_zombie_links?.length > 0 && (
                <div>
                  <strong>
                    {mlHealth.revoked_zombie_links.length} link "zombie" revocati
                  </strong>{" "}
                  (puntavano a clienti eliminati): reinvia il nuovo link dalla
                  scheda cliente.
                </div>
              )}
              {mlHealth.clients_without_salon_name?.length > 0 && (
                <div>
                  <strong>
                    {mlHealth.clients_without_salon_name.length} clienti senza
                    nome salone
                  </strong>{" "}
                  (saluto e cartella Dropbox usano il ripiego email/referente):
                  <ul className="list-disc ml-5 mt-1">
                    {mlHealth.clients_without_salon_name.slice(0, 10).map((c) => (
                      <li key={c.client_id}>
                        {c.email || c.client_id}
                        {c.referente ? ` (ref. ${c.referente})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {mlTickets && (
            <div
              data-testid="ml-tickets-list"
              className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4 text-sm space-y-3"
            >
              <div className="flex items-center gap-2 flex-wrap text-[12px]">
                {Object.entries(mlTickets.counts || {}).map(([status, n]) => (
                  <Badge
                    key={status}
                    variant="outline"
                    className="border-[var(--mp-border)] bg-white"
                  >
                    {MAGIC_LINK_STATUS[status]?.label || status}: {n}
                  </Badge>
                ))}
              </div>
              {(mlTickets.tickets || []).length === 0 ? (
                <div className="text-[var(--mp-muted)]">
                  Nessun tentativo di upload negli ultimi 7 giorni.
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-1.5">
                  {mlTickets.tickets.map((t) => {
                    const st = t.recovered
                      ? MAGIC_LINK_STATUS.recovered
                      : MAGIC_LINK_STATUS[t.status] || MAGIC_LINK_STATUS.unknown;
                    return (
                      <div
                        key={t.ticket_id}
                        data-testid={`ml-ticket-row-${t.ticket_id}`}
                        className="rounded-xl border border-[var(--mp-border)] bg-white px-3 py-2 flex items-center gap-3"
                      >
                        <span
                          className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${st.cls}`}
                        >
                          {st.label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium truncate">
                            {t.client_name || "Cliente"}{" "}
                            <span className="text-[var(--mp-muted)] font-normal">
                              · {t.filename}
                            </span>
                          </div>
                          <div className="text-[11px] text-[var(--mp-muted)]">
                            {t.size ? `${(t.size / (1024 * 1024)).toFixed(1)} MB · ` : ""}
                            {t.created_at
                              ? new Date(t.created_at).toLocaleString("it-IT", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Admin-only: Batch ACK diagnostic widget (debug Telegram cron) */}
      {isAdmin && <BatchAckDiagnostic />}

      {/* Admin-only: Telegram webhook management (re-register after deploy) */}
      {isAdmin && (
        <div
          data-testid="settings-telegram-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-5"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
            <Send className="h-3.5 w-3.5" /> Bot Telegram — webhook
          </div>

          <div className="text-sm text-[var(--mp-muted)] leading-relaxed">
            Dopo aver pubblicato l'app su un nuovo dominio (es.{" "}
            <code className="bg-[var(--mp-dash-bg)] px-1.5 rounded">
              portale.miosito.it
            </code>
            ), premi <strong>Ri-registra webhook</strong> per far sapere a
            Telegram dove inviare i messaggi ricevuti dal bot.
          </div>

          {webhookLoading ? (
            <div className="text-sm text-[var(--mp-muted)]">
              Controllo stato webhook…
            </div>
          ) : webhookInfo?.result ? (
            (() => {
              const r = webhookInfo.result;
              let currentHost = "";
              let webhookHost = "";
              try {
                currentHost = window.location.host;
                webhookHost = r.url ? new URL(r.url).host : "";
              } catch {
                /* noop */
              }
              const hostMismatch =
                !!r.url && !!currentHost && webhookHost !== currentHost;
              const healthy =
                !!r.url && !hostMismatch && !r.last_error_message;
              return (
                <div className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4 space-y-2 text-[13px]">
                  <div className="flex items-center gap-2">
                    {healthy ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    )}
                    <span className="font-medium">
                      {!r.url
                        ? "Webhook non registrato"
                        : hostMismatch
                        ? "Webhook su un altro dominio"
                        : r.last_error_message
                        ? "Webhook registrato — ultimo tentativo fallito"
                        : "Webhook attivo"}
                    </span>
                  </div>
                  {r.url && (
                    <div className="text-[11px] text-[var(--mp-muted)] break-all">
                      URL: {r.url}
                    </div>
                  )}
                  {hostMismatch && (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                      ⚠ Il webhook punta a <code>{webhookHost}</code> ma stai
                      usando <code>{currentHost}</code>. Premi{" "}
                      <strong>Ri-registra webhook</strong> per allinearlo al
                      dominio corrente.
                    </div>
                  )}
                  {r.last_error_message && !hostMismatch && (
                    <div className="text-[11px] text-red-600">
                      ⚠ Ultimo errore: {r.last_error_message}
                      <div className="text-[10px] text-[var(--mp-muted)] mt-0.5">
                        (si cancella automaticamente al primo messaggio ricevuto
                        con successo)
                      </div>
                    </div>
                  )}
                  {typeof r.pending_update_count === "number" &&
                    r.pending_update_count > 0 && (
                      <div className="text-[11px] text-[var(--mp-muted)]">
                        Update pendenti: {r.pending_update_count}
                      </div>
                    )}
                </div>
              );
            })()
          ) : (
            <div className="text-sm text-[var(--mp-muted)]">
              Nessuna informazione disponibile. Ri-registra per provare.
            </div>
          )}

          <div className="flex items-center justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={loadWebhook}
              disabled={webhookLoading}
              data-testid="settings-telegram-refresh"
              className="rounded-full border-[var(--mp-ink)]"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {webhookLoading ? "Controllo…" : "Controlla stato"}
            </Button>
            <Button
              onClick={registerWebhook}
              disabled={webhookSaving}
              data-testid="settings-telegram-register"
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <Send className="h-4 w-4 mr-2" />
              {webhookSaving ? "Registro…" : "Ri-registra webhook"}
            </Button>
            <Button
              onClick={recoverWebhook}
              disabled={webhookRecovering}
              variant="outline"
              data-testid="settings-telegram-recover"
              title="Forza il riavvio del webhook scartando i pending updates accumulati. Da usare dopo un outage del backend."
              className="rounded-full border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {webhookRecovering ? "Recupero…" : "Recupera (drop pending)"}
            </Button>
          </div>

          {/* Auto healthcheck status (hourly cron) */}
          <div
            data-testid="settings-webhook-healthcheck"
            className="rounded-2xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4 space-y-2 text-[12px]"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-bold uppercase tracking-[0.18em] text-[10px] text-[var(--mp-muted)]">
                Controllo automatico orario
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={runHealthcheckNow}
                disabled={healthChecking}
                data-testid="settings-webhook-healthcheck-now"
                className="h-7 rounded-full text-[11px] hover:bg-[var(--mp-ink)] hover:text-white"
              >
                {healthChecking ? "Controllo…" : "Esegui ora"}
              </Button>
            </div>
            <div className="text-[var(--mp-muted)] leading-relaxed">
              Ogni ora il sistema verifica che il webhook punti al dominio
              giusto e che non ci siano errori recenti. Se rileva una
              deviazione, la <strong>corregge in automatico</strong> e notifica
              la chat staff.
            </div>
            {webhookHealth?.checked_at ? (
              <div className="flex items-center gap-2 pt-1">
                {webhookHealth.healthy ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                )}
                <span className="text-[11px]">
                  Ultimo controllo:{" "}
                  {new Date(webhookHealth.checked_at).toLocaleString("it-IT", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  —{" "}
                  {webhookHealth.healed
                    ? "Deviazione corretta automaticamente ✓"
                    : webhookHealth.healthy
                    ? "OK"
                    : webhookHealth.drift
                    ? "Webhook deviato"
                    : webhookHealth.recent_error
                    ? "Errore recente"
                    : webhookHealth.too_many_pending
                    ? "Coda troppo lunga"
                    : "Anomalia"}
                </span>
              </div>
            ) : webhookHealth?.skipped ? (
              <div className="text-[11px] text-[var(--mp-muted)] italic">
                Questo non è il pod di produzione — il controllo automatico
                gira solo sul dominio finale.
              </div>
            ) : (
              <div className="text-[11px] text-[var(--mp-muted)] italic">
                Nessun controllo automatico ancora eseguito. Il primo parte al
                prossimo minuto :05.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin-only: Maintenance */}
      {isAdmin && (
        <MaintenanceCard />
      )}

      {/* GDPR self-service: consent, export, erasure */}
      <MediaRetentionClientCard />
      <PrivacyDataCard />

      {/* Read-only info */}
      <div className="rounded-3xl border border-[var(--mp-border)] bg-white divide-y divide-[var(--mp-border)]">
        <InfoRow
          icon={<Mail className="h-4 w-4" />}
          label="Email"
          value={user?.email}
          badge="Non modificabile"
        />
        <InfoRow
          icon={<Shield className="h-4 w-4" />}
          label="Ruolo"
          value={roleLabel}
          badge="Non modificabile"
        />
        {isClient && (
          <InfoRow
            icon={<Crown className="h-4 w-4" />}
            label="Piano"
            value={user?.plan === "pro" ? "Pro" : "Free"}
            badge={
              user?.plan === "pro"
                ? "Servizi premium attivi"
                : "Contatta l'agenzia per attivare Pro"
            }
          />
        )}
      </div>
    </div>
  );
}

function FieldBlock({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5">
        {label}
      </div>
      <Input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-full border-[var(--mp-border)] bg-white h-11"
      />
    </label>
  );
}

function InfoRow({ icon, label, value, badge }) {
  return (
    <div className="flex items-center justify-between gap-4 p-5">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-8 w-8 rounded-full bg-[var(--mp-dash-bg)] grid place-items-center text-[var(--mp-muted)] shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            {label}
          </div>
          <div className="font-display text-base tracking-tight truncate">
            {value || "—"}
          </div>
        </div>
      </div>
      {badge && (
        <Badge className="rounded-full bg-[var(--mp-dash-bg)] text-[var(--mp-muted)] text-[10px] uppercase tracking-[0.18em] hidden sm:inline-flex">
          {badge}
        </Badge>
      )}
    </div>
  );
}


function MaintenanceCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/maintenance/telegram-duplicates");
      setReport(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const cleanup = async () => {
    if (!report) return;
    if (
      !window.confirm(
        `Confermi la cancellazione di ${report.messages_to_delete} messaggi duplicati? Verrà mantenuto il record più vecchio per ogni gruppo. Operazione non reversibile.`
      )
    )
      return;
    setRunning(true);
    try {
      const { data } = await api.post(
        "/admin/maintenance/telegram-duplicates/cleanup"
      );
      toast.success(
        `Pulizia completata: ${data.messages_deleted} messaggi rimossi`
      );
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid="settings-maintenance-card"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-5"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
        <Wrench className="h-3.5 w-3.5" /> Manutenzione database
      </div>
      <div className="text-sm text-[var(--mp-muted)] leading-relaxed">
        Strumenti una tantum per pulire dati legacy. Da usare solo se necessario.
      </div>

      <div className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4 space-y-2">
        <div className="font-display text-base tracking-tight">
          Duplicati Telegram (legacy)
        </div>
        <div className="text-xs text-[var(--mp-muted)]">
          Stesso video Telegram ricevuto due volte da prima dell'introduzione del controllo
          anti-duplicazione. Mantiene il record più vecchio, cancella gli altri.
        </div>

        {loading ? (
          <div className="text-sm text-[var(--mp-muted)]">Carico…</div>
        ) : report ? (
          <div className="grid grid-cols-3 gap-3 pt-2">
            <Stat
              label="Gruppi duplicati"
              value={report.duplicate_groups}
              testId="maint-dups-groups"
            />
            <Stat
              label="Da eliminare"
              value={report.messages_to_delete}
              tone={report.messages_to_delete > 0 ? "warn" : "ok"}
              testId="maint-dups-delete"
            />
            <Stat
              label="Indice protezione"
              value={report.index_present ? "Attivo" : "Mancante"}
              tone={report.index_present ? "ok" : "warn"}
              testId="maint-dups-index"
            />
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading || running}
            data-testid="maint-refresh"
            className="rounded-full border-[var(--mp-ink)]"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
          </Button>
          {report && report.messages_to_delete > 0 && (
            <Button
              onClick={cleanup}
              disabled={running}
              data-testid="maint-cleanup"
              className="rounded-full bg-[var(--mp-primary)] hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {running ? "Pulizia in corso…" : `Pulisci ${report.messages_to_delete} duplicati`}
            </Button>
          )}
          {report && report.messages_to_delete === 0 && (
            <Badge className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] uppercase tracking-[0.18em]">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Nessun duplicato
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "default", testId }) {
  const toneCls = {
    default: "bg-white border-[var(--mp-border)]",
    ok: "bg-emerald-50 border-emerald-300 text-emerald-900",
    warn: "bg-amber-50 border-amber-300 text-amber-900",
    muted: "bg-[var(--mp-dash-bg)] border-[var(--mp-border)] text-[var(--mp-muted)]",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border p-3 ${toneCls[tone]}`}
    >
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] opacity-70">
        {label}
      </div>
      <div className="font-display text-2xl tracking-tight mt-0.5">{value}</div>
    </div>
  );
}
